import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	Box,
	Tabs,
	Tab,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	Typography,
	Stack,
	Divider,
	List,
	ListItem,
	ListItemText,
	IconButton,
	Tooltip,
} from "@mui/material";
import { Refresh, Add, Visibility, CheckCircleOutline, CancelOutlined } from "@mui/icons-material";
import {
	BackofficeAdminServices,
	NotistackServices,
	type TemplateProposalDTO,
	type McpProposalDTO,
	type ProposalStatus,
} from "shared/services";
import { HandleError } from "shared/utils";
import styles from "./styles.module.scss";

type TabKey = "templates" | "mcps";

const STATUS_FILTERS: Array<{ value: ProposalStatus; label: string }> = [
	{ value: "pending_review", label: "Pendentes" },
	{ value: "approved", label: "Aprovadas" },
	{ value: "rejected", label: "Rejeitadas" },
];

export const DevBotProposalsPage: React.FC = () => {
	const [tab, setTab] = useState<TabKey>("templates");
	const [status, setStatus] = useState<ProposalStatus>("pending_review");
	const [templates, setTemplates] = useState<TemplateProposalDTO[]>([]);
	const [mcps, setMcps] = useState<McpProposalDTO[]>([]);
	const [loading, setLoading] = useState(true);
	const [selectedTemplate, setSelectedTemplate] = useState<TemplateProposalDTO | null>(null);
	const [selectedMcp, setSelectedMcp] = useState<McpProposalDTO | null>(null);
	const [proposeOpen, setProposeOpen] = useState(false);
	const [proposeText, setProposeText] = useState("");
	const [proposing, setProposing] = useState(false);

	const load = useCallback(async () => {
		setLoading(true);
		try {
			if (tab === "templates") {
				const res = await BackofficeAdminServices.listTemplateProposals({ status, limit: 100 });
				setTemplates(res.items);
			} else {
				const res = await BackofficeAdminServices.listMcpProposals({ status, limit: 100 });
				setMcps(res.items);
			}
		} catch (error) {
			NotistackServices.showErrorMessage(HandleError.isHandleError(error) ? error.message : "Erro ao listar propostas");
		} finally {
			setLoading(false);
		}
	}, [tab, status]);

	useEffect(() => {
		load();
	}, [load]);

	const handlePropose = async () => {
		if (!proposeText.trim() || proposeText.trim().length < 3) {
			NotistackServices.showErrorMessage("Descreva o gap em pelo menos 3 caracteres.");
			return;
		}
		setProposing(true);
		try {
			if (tab === "templates") {
				await BackofficeAdminServices.createTemplateProposal(proposeText.trim());
			} else {
				await BackofficeAdminServices.createMcpProposal(proposeText.trim());
			}
			NotistackServices.showSuccessMessage("Proposta gerada pelo DevBot!");
			setProposeOpen(false);
			setProposeText("");
			load();
		} catch (error) {
			NotistackServices.showErrorMessage(HandleError.isHandleError(error) ? error.message : "Erro ao gerar proposta");
		} finally {
			setProposing(false);
		}
	};

	const handleApprove = async (id: string, kind: TabKey) => {
		try {
			if (kind === "templates") {
				const res = await BackofficeAdminServices.approveTemplateProposal(id);
				NotistackServices.showSuccessMessage(`Template criado (id ${res.createdMemberLibraryId}, ${res.mcpVinculosCount} MCPs vinculados).`);
			} else {
				const res = await BackofficeAdminServices.approveMcpProposal(id);
				NotistackServices.showSuccessMessage(`MCP criado (id ${res.createdMcpLibraryId}). ${res.note}`);
			}
			setSelectedTemplate(null);
			setSelectedMcp(null);
			load();
		} catch (error) {
			NotistackServices.showErrorMessage(HandleError.isHandleError(error) ? error.message : "Erro ao aprovar");
		}
	};

	const handleReject = async (id: string, kind: TabKey) => {
		const notes = window.prompt("Motivo da rejeição (opcional):") ?? undefined;
		try {
			if (kind === "templates") {
				await BackofficeAdminServices.rejectTemplateProposal(id, notes);
			} else {
				await BackofficeAdminServices.rejectMcpProposal(id, notes);
			}
			NotistackServices.showSuccessMessage("Proposta rejeitada.");
			setSelectedTemplate(null);
			setSelectedMcp(null);
			load();
		} catch (error) {
			NotistackServices.showErrorMessage(HandleError.isHandleError(error) ? error.message : "Erro ao rejeitar");
		}
	};

	const rowCount = tab === "templates" ? templates.length : mcps.length;

	return (
		<div className={styles.container}>
			<div className={styles.header}>
				<div>
					<Typography variant="h5" className={styles.title}>DevBot Proposals</Typography>
					<Typography variant="body2" className={styles.subtitle}>
						Propostas backend de novos templates de perfis profissionais (RH) e novos MCPs. Geradas por LLM Sonnet 4.5 sob demanda;
						revisadas aqui antes de virarem registros públicos.
					</Typography>
				</div>
				<Stack direction="row" spacing={1}>
					<Tooltip title="Recarregar">
						<IconButton onClick={load}><Refresh /></IconButton>
					</Tooltip>
					<Button startIcon={<Add />} variant="contained" onClick={() => setProposeOpen(true)}>
						Propor nova
					</Button>
				</Stack>
			</div>

			<Tabs value={tab} onChange={(_, v) => setTab(v)} className={styles.tabs}>
				<Tab value="templates" label="Templates (DevBot RH)" />
				<Tab value="mcps" label="MCPs (DevBot MCP)" />
			</Tabs>

			<Stack direction="row" spacing={1} className={styles.filters}>
				{STATUS_FILTERS.map((f) => (
					<Chip key={f.value} label={f.label} color={status === f.value ? "primary" : "default"} onClick={() => setStatus(f.value)} />
				))}
				<Box sx={{ flex: 1 }} />
				<Typography variant="caption">{rowCount} {rowCount === 1 ? "item" : "itens"}</Typography>
			</Stack>

			{loading ? (
				<Box className={styles.loading}><CircularProgress /></Box>
			) : tab === "templates" ? (
				<TemplateProposalsList items={templates} onOpen={setSelectedTemplate} />
			) : (
				<McpProposalsList items={mcps} onOpen={setSelectedMcp} />
			)}

			<TemplateProposalDialog
				proposal={selectedTemplate}
				onClose={() => setSelectedTemplate(null)}
				onApprove={(id) => handleApprove(id, "templates")}
				onReject={(id) => handleReject(id, "templates")}
			/>

			<McpProposalDialog
				proposal={selectedMcp}
				onClose={() => setSelectedMcp(null)}
				onApprove={(id) => handleApprove(id, "mcps")}
				onReject={(id) => handleReject(id, "mcps")}
			/>

			<Dialog open={proposeOpen} onClose={() => setProposeOpen(false)} maxWidth="sm" fullWidth>
				<DialogTitle>Propor {tab === "templates" ? "novo template (DevBot RH)" : "novo MCP (DevBot MCP)"}</DialogTitle>
				<DialogContent>
					<Typography variant="body2" sx={{ mb: 2 }}>
						Descreva o gap em PT-BR. Ex.: {tab === "templates"
							? '"Especialista em Social Media Manager B2B com foco em LinkedIn e métricas de engajamento"'
							: '"Integração com Asana para gerenciar tarefas e projetos via API"'}.
					</Typography>
					<TextField
						autoFocus
						multiline
						minRows={3}
						fullWidth
						value={proposeText}
						onChange={(e) => setProposeText(e.target.value)}
						placeholder="Descreva o gap..."
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setProposeOpen(false)} disabled={proposing}>Cancelar</Button>
					<Button onClick={handlePropose} variant="contained" disabled={proposing}>
						{proposing ? "Gerando..." : "Gerar proposta"}
					</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
};

// ─── Listas ────────────────────────────────────────────────────────

const TemplateProposalsList: React.FC<{ items: TemplateProposalDTO[]; onOpen: (p: TemplateProposalDTO) => void }> = ({ items, onOpen }) => {
	if (items.length === 0) {
		return <Box className={styles.empty}>Nenhuma proposta.</Box>;
	}
	return (
		<List>
			{items.map((p) => (
				<ListItem key={p.id} className={styles.row} secondaryAction={
					<IconButton edge="end" onClick={() => onOpen(p)}><Visibility /></IconButton>
				} onClick={() => onOpen(p)} onMouseEnter={() => {}} style={{cursor:"pointer"}}>
					<ListItemText
						primary={<><strong>{p.proposedName}</strong> <code className={styles.slug}>{p.proposedSlug}</code> <Chip size="small" label={`Tier ${p.proposedTier}`} /></>}
						secondary={
							<>
								<span>{p.proposedDescription.slice(0, 160)}{p.proposedDescription.length > 160 ? "…" : ""}</span>
								<br />
								<small>Trigger: {p.triggeredBy} · MCPs: {p.proposedMcpSlugs.join(", ") || "—"} · {p.llmModel} · {new Date(p.createdAt).toLocaleString("pt-BR")}</small>
							</>
						}
					/>
				</ListItem>
			))}
		</List>
	);
};

const McpProposalsList: React.FC<{ items: McpProposalDTO[]; onOpen: (p: McpProposalDTO) => void }> = ({ items, onOpen }) => {
	if (items.length === 0) {
		return <Box className={styles.empty}>Nenhuma proposta.</Box>;
	}
	return (
		<List>
			{items.map((p) => (
				<ListItem key={p.id} className={styles.row} secondaryAction={
					<IconButton edge="end" onClick={() => onOpen(p)}><Visibility /></IconButton>
				} onClick={() => onOpen(p)} onMouseEnter={() => {}} style={{cursor:"pointer"}}>
					<ListItemText
						primary={<><strong>{p.proposedName}</strong> <code className={styles.slug}>{p.proposedSlug}</code> <Chip size="small" label={p.proposedCategory} /></>}
						secondary={
							<>
								<span>{p.proposedDescription.slice(0, 160)}{p.proposedDescription.length > 160 ? "…" : ""}</span>
								<br />
								<small>Trigger: {p.triggeredBy} · OAuth: {p.proposedOauthConfig?.provider ?? "—"} · {p.proposedTools.length} tools · {new Date(p.createdAt).toLocaleString("pt-BR")}</small>
							</>
						}
					/>
				</ListItem>
			))}
		</List>
	);
};

// ─── Dialogs de detalhe ────────────────────────────────────────────

const TemplateProposalDialog: React.FC<{
	proposal: TemplateProposalDTO | null;
	onClose: () => void;
	onApprove: (id: string) => void;
	onReject: (id: string) => void;
}> = ({ proposal, onClose, onApprove, onReject }) => {
	const sections = useMemo(() => {
		if (!proposal?.checklist) return [];
		return Object.entries(proposal.checklist).map(([key, section]) => ({ key, ...section }));
	}, [proposal]);

	if (!proposal) return null;
	const isPending = proposal.status === "pending_review";

	return (
		<Dialog open={!!proposal} onClose={onClose} maxWidth="lg" fullWidth>
			<DialogTitle>
				<Stack direction="row" spacing={2} alignItems="center">
					<Box sx={{ flex: 1 }}>
						<Typography variant="h6">{proposal.proposedName}</Typography>
						<Typography variant="caption">Slug: <code>{proposal.proposedSlug}</code> · Tier {proposal.proposedTier} · {proposal.llmModel}</Typography>
					</Box>
					<Chip label={proposal.status} color={proposal.status === "approved" ? "success" : proposal.status === "rejected" ? "error" : "warning"} />
				</Stack>
			</DialogTitle>
			<DialogContent>
				<Stack spacing={2}>
					<Section title="Descrição">
						<Typography>{proposal.proposedDescription}</Typography>
					</Section>
					<Section title="System Prompt">
						<pre className={styles.code}>{proposal.proposedSystemPrompt}</pre>
					</Section>
					<Section title="Senioridade & Pricing">
						<pre className={styles.code}>{JSON.stringify(proposal.proposedSeniorityConfig, null, 2)}</pre>
					</Section>
					<Section title="Competências">
						<pre className={styles.code}>{JSON.stringify(proposal.proposedCompetencies, null, 2)}</pre>
					</Section>
					<Section title={`MCPs (${proposal.proposedMcpSlugs.length})`}>
						{proposal.proposedMcpSlugs.length === 0 ? <em>Nenhum.</em> : proposal.proposedMcpSlugs.map((s) => <Chip key={s} label={s} sx={{ mr: 0.5 }} />)}
					</Section>
					<Section title={`Casos de uso (${proposal.proposedUseCases.length})`}>
						{proposal.proposedUseCases.map((uc, i) => (
							<Box key={i} sx={{ mb: 1 }}>
								<Typography variant="subtitle2">{uc.title}</Typography>
								<Typography variant="body2">{uc.context}</Typography>
								<Typography variant="caption" color="success.main">→ {uc.expected_outcome}</Typography>
							</Box>
						))}
					</Section>
					<Section title={`Entregáveis (${proposal.proposedDeliverables.length})`}>
						{proposal.proposedDeliverables.map((d, i) => <Chip key={i} label={d} sx={{ mr: 0.5, mb: 0.5 }} />)}
					</Section>
					<Section title="Checklist 10 etapas (plano v4 §3.5.1)">
						{sections.map((s) => (
							<Box key={s.key} sx={{ mb: 1.5 }}>
								<Typography variant="subtitle2">{s.key.replace(/_/g, " ")}</Typography>
								{s.summary && <Typography variant="body2" color="textSecondary">{s.summary}</Typography>}
								<List dense>
									{s.items.map((it, idx) => (
										<ListItem key={idx} disablePadding>
											<ListItemText
												primary={<>{it.checked ? "✓" : "✗"} {it.label}</>}
												secondary={it.note}
											/>
										</ListItem>
									))}
								</List>
							</Box>
						))}
					</Section>
					{proposal.reviewNotes && <Section title="Notas da revisão"><Typography>{proposal.reviewNotes}</Typography></Section>}
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Fechar</Button>
				{isPending && (
					<>
						<Button startIcon={<CancelOutlined />} color="error" onClick={() => onReject(proposal.id)}>Rejeitar</Button>
						<Button startIcon={<CheckCircleOutline />} color="success" variant="contained" onClick={() => onApprove(proposal.id)}>Aprovar</Button>
					</>
				)}
			</DialogActions>
		</Dialog>
	);
};

const McpProposalDialog: React.FC<{
	proposal: McpProposalDTO | null;
	onClose: () => void;
	onApprove: (id: string) => void;
	onReject: (id: string) => void;
}> = ({ proposal, onClose, onApprove, onReject }) => {
	if (!proposal) return null;
	const isPending = proposal.status === "pending_review";

	return (
		<Dialog open={!!proposal} onClose={onClose} maxWidth="lg" fullWidth>
			<DialogTitle>
				<Stack direction="row" spacing={2} alignItems="center">
					<Box sx={{ flex: 1 }}>
						<Typography variant="h6">{proposal.proposedName}</Typography>
						<Typography variant="caption">Slug: <code>{proposal.proposedSlug}</code> · {proposal.proposedCategory} · {proposal.llmModel}</Typography>
					</Box>
					<Chip label={proposal.status} color={proposal.status === "approved" ? "success" : proposal.status === "rejected" ? "error" : "warning"} />
				</Stack>
			</DialogTitle>
			<DialogContent>
				<Stack spacing={2}>
					<Section title="Descrição"><Typography>{proposal.proposedDescription}</Typography></Section>
					<Section title="System Prompt"><pre className={styles.code}>{proposal.proposedSystemPrompt}</pre></Section>
					<Section title="OAuth"><pre className={styles.code}>{JSON.stringify(proposal.proposedOauthConfig, null, 2)}</pre></Section>
					<Section title="API"><pre className={styles.code}>{JSON.stringify(proposal.proposedApiConfig, null, 2)}</pre></Section>
					<Section title={`Tools (${proposal.proposedTools.length})`}>
						{proposal.proposedTools.map((t, i) => (
							<Box key={i} sx={{ mb: 1 }}>
								<Typography variant="subtitle2">
									<code>{t.name}</code> {t.confirmation_required && <Chip size="small" label="confirma" />}
								</Typography>
								<Typography variant="body2">{t.description}</Typography>
								{t.path && <Typography variant="caption">{t.method ?? "GET"} {t.path}</Typography>}
							</Box>
						))}
					</Section>
					<Section title="tool_env_fields"><pre className={styles.code}>{JSON.stringify(proposal.proposedToolEnvFields, null, 2)}</pre></Section>
					<Section title="Spec analysis"><pre className={styles.code}>{JSON.stringify(proposal.specAnalysis, null, 2)}</pre></Section>
					{proposal.reviewNotes && <Section title="Notas da revisão"><Typography>{proposal.reviewNotes}</Typography></Section>}
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Fechar</Button>
				{isPending && (
					<>
						<Button startIcon={<CancelOutlined />} color="error" onClick={() => onReject(proposal.id)}>Rejeitar</Button>
						<Button startIcon={<CheckCircleOutline />} color="success" variant="contained" onClick={() => onApprove(proposal.id)}>Aprovar</Button>
					</>
				)}
			</DialogActions>
		</Dialog>
	);
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
	<Box>
		<Typography variant="overline" color="textSecondary">{title}</Typography>
		<Divider sx={{ mb: 1 }} />
		{children}
	</Box>
);
